import { CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { FloatingButton, FloatingButtonsContainer } from './styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { RefObject, useState } from 'react';
import { Tooltip } from '@mui/material';

interface FloatingButtonProps {
  variant: 'risk' | 'opportunity';
  onClick: (variant: 'risk' | 'opportunity') => void;
  ref?: RefObject<HTMLDivElement | null>;
  className?: string;
  id?: string;
  loading?: boolean;
  disabled?: {
    risk?: boolean;
    opportunity?: boolean;
  };
  isStatic?: boolean;
}

export const FloatingButtons = ({
  variant,
  onClick,
  ref,
  className,
  id,
  loading,
  disabled,
  isStatic,
}: FloatingButtonProps) => {
  const theme = useTheme();

  const { t } = useTranslation();

  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null);

  const handleOpenTooltip = (index: number) => {
    setOpenTooltipIndex(index);
  };

  const handleCloseTooltip = () => {
    setOpenTooltipIndex(null);
  };

  return (
    <FloatingButtonsContainer ref={ref} className={className} id={id} isStatic={isStatic}>
      <Tooltip
        open={openTooltipIndex === 0}
        onClose={handleCloseTooltip}
        title={t('label.label.no_opportunity_calculated')}
        placement="top"
        arrow
        disableHoverListener={!disabled?.opportunity}
      >
        <span onClick={() => disabled?.opportunity && handleOpenTooltip(0)}>
          <FloatingButton
            isActive={variant === 'opportunity'}
            onClick={() => onClick('opportunity')}
            textColor={theme.palette.primary.main}
            disabled={disabled?.opportunity || loading}
          >
            <CheckCircleOutlined color="inherit" />
            <SafeMarkdown text={t('dimension.dimension.opportunity')} />
          </FloatingButton>
        </span>
      </Tooltip>

      <Tooltip
        open={openTooltipIndex === 1}
        onClose={handleCloseTooltip}
        title={t('label.label.no_risk_calculated')}
        placement="top"
        arrow
        disableHoverListener={!disabled?.risk}
      >
        <span onClick={() => disabled?.risk && handleOpenTooltip(1)}>
          <FloatingButton
            isActive={variant === 'risk'}
            onClick={() => onClick('risk')}
            textColor={theme.palette.orange[800]}
            disabled={disabled?.risk || loading}
          >
            <WarningAmberOutlined color="inherit" />
            <SafeMarkdown text={t('dimension.dimension.risk')} />
          </FloatingButton>
        </span>
      </Tooltip>
    </FloatingButtonsContainer>
  );
};
